import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PODIUM_URL } from "@/lib/assets";
import { PlayCircle, Lock } from "lucide-react";

const badgeForAccess = (a) => a === "public" ? "Public" : a === "free" ? "Free Members" : a === "paid" ? "Paid Members" : "Purchase";

export function VideosList() {
  const [videos, setVideos] = useState([]);
  useEffect(() => { api.get("/videos").then(r => setVideos(r.data)); }, []);
  const featured = videos.filter(v => v.featured);
  const rest = videos.filter(v => !v.featured);
  return (
    <div className="bg-cream">
      <div className="brand-container py-20">
        <div className="brand-eyebrow">Video Library</div>
        <h1 className="brand-h1 mt-3 mb-4">The floor is Auntie's.</h1>
        <p className="brand-lede max-w-2xl">Firesides, reflections, and the full <em>Five Takeaways</em> series. Public sessions are free to watch — some require a membership or ticket.</p>

        {featured.length > 0 && (
          <section className="mt-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="gold-rule" />
              <div className="brand-eyebrow">Featured Speech</div>
              <div className="gold-rule" />
            </div>
            {featured.map(v => (
              <div key={v.id} className="editorial-card overflow-hidden mb-6" data-testid={`featured-video-${v.id}`}>
                <div className="grid lg:grid-cols-12 gap-0">
                  <div className="lg:col-span-8 relative bg-charcoal">
                    <div className="aspect-video">
                      <iframe src={v.embed_url} title={v.title} className="w-full h-full" allowFullScreen frameBorder="0" />
                    </div>
                  </div>
                  <div className="lg:col-span-4 p-8 flex flex-col justify-center">
                    <div className="brand-eyebrow">{v.category}</div>
                    <h2 className="font-serif text-3xl leading-tight text-charcoal mt-3">{v.title}</h2>
                    <p className="mt-4 text-charcoal/70 text-sm leading-relaxed">{v.description}</p>
                    <Link to={`/videos/${v.id}`} className="btn-primary mt-6 inline-flex text-xs w-fit" data-testid={`featured-open-${v.id}`}>Open full page</Link>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        <h2 className="brand-h2 mt-16 mb-8">All videos</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rest.map((v) => (
            <Link to={`/videos/${v.id}`} key={v.id} className="editorial-card block" data-testid={`video-card-${v.id}`}>
              <div className="relative aspect-video overflow-hidden">
                <img src={v.thumbnail || PODIUM_URL} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center bg-plum/10">
                  {v.access_level === "public" || v.access_level === "free" ? <PlayCircle className="w-14 h-14 text-cream drop-shadow" /> : <Lock className="w-10 h-10 text-cream drop-shadow" />}
                </div>
                <span className="absolute top-3 left-3 bg-cream/90 text-plum text-[10px] uppercase tracking-widest px-2 py-1 rounded-full font-bold">{badgeForAccess(v.access_level)}</span>
              </div>
              <div className="p-5">
                <div className="brand-eyebrow">{v.category}</div>
                <h3 className="mt-2 font-serif text-xl leading-tight text-charcoal">{v.title}</h3>
                <p className="mt-1 text-xs text-charcoal/50">{v.duration_min ? `${v.duration_min} min` : ""}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export function VideoDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get(`/videos/${id}`).then(r => setV(r.data)).catch(() => setErr("Video not found")); }, [id]);
  if (err) return <div className="brand-container py-20 text-charcoal/60">{err}</div>;
  if (!v) return <div className="brand-container py-20 text-charcoal/40">Loading…</div>;
  return (
    <div className="bg-cream">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <Link to="/videos" className="text-sm text-plum hover:underline" data-testid="video-back-link">← Back to videos</Link>
        <div className="brand-eyebrow mt-8">{v.category}</div>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight text-charcoal mt-3">{v.title}</h1>
        <p className="mt-4 text-charcoal/70">{v.description}</p>
        <div className="aspect-video mt-8 rounded-lg overflow-hidden border border-gold-light bg-charcoal">
          {v.can_play && v.embed_url ? (
            <iframe src={v.embed_url} title={v.title} className="w-full h-full" allowFullScreen frameBorder="0" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-cream p-10 text-center">
              <Lock className="w-12 h-12 text-gold" />
              <div className="font-serif text-2xl">This session is reserved for {badgeForAccess(v.access_level).toLowerCase()}.</div>
              <Link to="/membership" className="btn-outline !border-gold !text-cream" data-testid="video-upgrade-btn">Enter the Chambers</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
